
import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';

import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import Swal from 'sweetalert2';
import { getSignaturePackages, getSignatureBalance, createSignatureCheckout, getSignaturePurchase, signatureCreditError, signatureQuote } from '../services/api_firme';

const MAX_PACKAGES = 1000;

const formatCurrency = value =>
  (Number(value) / 100).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ModalBuyFirmeCredits({
  open,
  onClose,
  availableCredits = null,
  useFreeCredits = false,
  token,
  storageScope,
  enabled = true,
  onBalance,
}) {
  // PACCHETTO SELEZIONATO
  const [packageId, setPackageId] = useState(null);
  const [packages, setPackages] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const busy = useRef(false);
  const storageKey = `signature-checkout:${storageScope}`;

  // NUMERO DI PACCHETTI DA ACQUISTARE
  const [packageCount, setPackageCount] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // PACCHETTO SELEZIONATO
  const selectedPackage = packages.find(pkg => pkg.id === packageId);

  // PREZZO DI UN PACCHETTO
  const packagePrice = selectedPackage?.net_unit_cents ?? null;

  // PREZZO MEDIO PER FIRMA
  const packageUnitPrice = selectedPackage
    ? Math.round(selectedPackage.net_unit_cents / selectedPackage.credits_per_package)
    : null;

  // NUMERO TOTALE FIRME ACQUISTATE
  const totalSignatures = selectedPackage
    ? selectedPackage.credits_per_package * packageCount
    : 0;

  // TOTALE LORDO
  const subtotal = selectedPackage
    ? signatureQuote(selectedPackage, packageCount).gross
    : null;

  const validPackageCount =
    Number.isSafeInteger(packageCount) &&
    packageCount >= 1 &&
    packageCount <= MAX_PACKAGES;

  const canCheckout =
    enabled && !!token && !catalogLoading &&
    !!selectedPackage &&
    validPackageCount &&
    !isLoading;

  const quantity = selectedPackage?.credits_per_package ?? 0;
  const quote = selectedPackage ? signatureQuote(selectedPackage, packageCount) : null;

  useEffect(() => {
    if (!open || !token || !enabled) return;
    const controller = new AbortController();
    setCatalogLoading(true);
    setError('');
    getSignaturePackages(token, controller.signal).then(data => {
      if (controller.signal.aborted) return;
      setPackages(data);
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved?.payload && data.some(pkg => pkg.id === saved.payload.package_id)) {
        setPackageId(saved.payload.package_id);
        setPackageCount(saved.payload.quantity);
      } else {
        setPackageId(current => data.some(pkg => pkg.id === current) ? current : data[0]?.id ?? null);
      }
    }).catch(err => {
      if (!controller.signal.aborted) setError(signatureCreditError(err));
    }).finally(() => { if (!controller.signal.aborted) setCatalogLoading(false); });
    getSignatureBalance(token, controller.signal).then(data => {
      if (!controller.signal.aborted) onBalance?.(useFreeCredits ? data.free : data.company_credits ?? data.free);
    }).catch(err => { if (!controller.signal.aborted) setError(signatureCreditError(err)); });
    return () => controller.abort();
  }, [open, token, enabled, reload, storageKey, useFreeCredits]);

  // Un controllo al ritorno, più eventuali verifiche manuali: nessun polling.
  useEffect(() => {
    if (!token || !enabled) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('signature_payment')) return;
    let active = true;
    const check = async () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (!saved?.purchase_id) throw new Error('missing purchase');
        const purchase = await getSignaturePurchase(token, saved.purchase_id);
        if (!active) return;
        const cancelled = url.searchParams.get('signature_payment') === 'cancel';
        await Swal.fire({
          icon: purchase.status === 'COMPLETED' ? 'success' : purchase.status === 'FAILED' ? 'error' : 'info',
          title: purchase.status === 'COMPLETED' ? 'Acquisto completato' : purchase.status === 'FAILED' ? 'Pagamento non riuscito' : cancelled ? 'Checkout annullato' : 'Pagamento in attesa',
          text: purchase.status === 'PENDING' ? 'Il pagamento non è ancora confermato. Puoi verificare nuovamente lo stato ricaricando questa pagina.' : purchase.status === 'COMPLETED' ? 'Il pagamento è stato confermato.' : 'Il pagamento è fallito o scaduto. Puoi effettuare un nuovo acquisto.',
        });
        if (!active) return;
        if (purchase.status !== 'PENDING') {
          sessionStorage.removeItem(storageKey);
          url.searchParams.delete('signature_payment');
          url.searchParams.delete('signature_session_id');
          window.history.replaceState(null, '', url);
        }
        const balance = await getSignatureBalance(token);
        if (active) onBalance?.(useFreeCredits ? balance.free : balance.company_credits ?? balance.free);
      } catch (err) {
        if (active) Swal.fire({ icon: 'error', title: 'Verifica acquisto non riuscita', text: signatureCreditError(err) });
      }
    };
    check();
    return () => { active = false; };
  }, [token, enabled, storageKey, useFreeCredits]);

  // CAMBIO PACCHETTO
  const handleSelectPackage = selectedQuantity => {
    if (isLoading) return;

    setPackageId(selectedQuantity);
    setPackageCount(1);
    setError('');
  };

  // MODIFICA NUMERO PACCHETTI
  const updatePackageCount = value => {
    if (isLoading) return;

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) return;

    setPackageCount(
      Math.max(
        1,
        Math.min(MAX_PACKAGES, Math.trunc(numericValue))
      )
    );

    setError('');
  };

  // CONFERMA ACQUISTO
  const handleConfirm = async () => {
    if (!canCheckout) return;

    if (busy.current) return;
    busy.current = true;
    setIsLoading(true);
    setError('');
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved?.purchase_id) {
        const previous = await getSignaturePurchase(token, saved.purchase_id);
        if (previous.status !== 'PENDING') {
          sessionStorage.removeItem(storageKey);
          const balance = await getSignatureBalance(token);
          onBalance?.(useFreeCredits ? balance.free : balance.company_credits ?? balance.free);
          await Swal.fire({ icon: 'info', title: previous.status === 'COMPLETED' ? 'Acquisto già completato' : 'Pagamento precedente scaduto o fallito', text: 'Il saldo è stato aggiornato. Puoi ora effettuare un nuovo acquisto.' });
          return;
        }
      }
      let operation = saved;
      if (!operation || operation.payload.package_id !== packageId || operation.payload.quantity !== packageCount) {
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete('signature_session_id');
        returnUrl.searchParams.set('signature_payment', 'success');
        returnUrl.searchParams.set('signature_session_id', '{CHECKOUT_SESSION_ID}');
        const success = returnUrl.href.replace('%7BCHECKOUT_SESSION_ID%7D', '{CHECKOUT_SESSION_ID}');
        returnUrl.searchParams.delete('signature_session_id');
        returnUrl.searchParams.set('signature_payment', 'cancel');
        operation = { payload: { package_id: packageId, quantity: packageCount,
          idempotency_key: crypto.randomUUID(), success_url: success, cancel_url: returnUrl.href } };
        sessionStorage.setItem(storageKey, JSON.stringify(operation));
      }
      const response = await createSignatureCheckout(token, operation.payload);
      sessionStorage.setItem(storageKey, JSON.stringify({ ...operation, purchase_id: response.purchase_id }));
      if (!response.url || !response.purchase_id) throw new Error('Invalid checkout');
      window.location.assign(response.url);
    } catch (err) {
      const message = signatureCreditError(err);
      setError(message);
      Swal.fire({ icon: 'error', title: 'Acquisto non riuscito', text: message });
    } finally {
      busy.current = false;
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="relative z-[999]"
    >
      {/* OVERLAY */}
      <div
        className="fixed inset-0 bg-gray-900/50"
        aria-hidden="true"
      />

      {/* CONTENITORE MODALE */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">

          <Dialog.Panel className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">

            {/* INTESTAZIONE PRINCIPALE */}
            <div
              className="relative shrink-0 border-b border-indigo-200 p-4
                bg-white/60 backdrop-blur
                border border-indigo-100 shadow-sm
                before:absolute before:inset-0 before:-z-10
                before:bg-gradient-to-r before:from-indigo-300 before:via-sky-300 before:to-purple-300
                before:opacity-30"
            >
              <div className="flex items-start justify-between gap-4">

                <div className="flex items-center gap-3">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100">
                    <ShoppingCartOutlinedIcon
                      sx={{ fontSize: 24 }}
                      className="text-indigo-700"
                    />
                  </div>

                  <div className="min-w-0">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">
                      CONSULTALA HR
                    </span>

                    <Dialog.Title className="text-base font-bold uppercase text-indigo-800 sm:text-lg">
                      Acquista firme FEA
                    </Dialog.Title>

                    <p className="mt-0.5 text-xs text-gray-500">
                      Acquista nuove firme elettroniche avanzate
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  aria-label="Chiudi modale"
                  className="rounded-lg p-1.5 text-gray-400 transition hover:bg-indigo-100 hover:text-indigo-700 disabled:opacity-50"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            </div>

            {/* CONTENUTO SCORREVOLE */}
            <div className="flex-1 space-y-3 overflow-y-auto p-4 sm:p-5">

              {/* CREDITI DISPONIBILI */}
              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white">

                <div className="border-b border-indigo-100 bg-indigo-50/50 px-4 py-2 sm:px-5">
                  <h3 className="text-sm font-semibold uppercase text-indigo-800">
                    Situazione firme FEA
                  </h3>

                  <p className="pt-0.5 text-xs text-gray-500">
                    Riepilogo dei crediti attualmente disponibili
                  </p>
                </div>

                <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                  <span className="text-sm font-medium text-gray-600">
                    Firme disponibili
                  </span>

                  <span className="text-xl font-bold text-indigo-800">
                    {availableCredits ?? '—'}
                  </span>
                </div>
              </div>

              {/* SELEZIONE PACCHETTI */}
              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white">

                <div className="border-b border-indigo-100 bg-indigo-50/50 px-4 py-2 sm:px-5">
                  <h3 className="text-sm font-semibold uppercase text-indigo-800">
                    Seleziona pacchetto firme
                  </h3>

                  <p className="mt-1 text-xs text-gray-500">
                    Scegli il pacchetto FEA da acquistare e indica il numero di pacchetti desiderati
                  </p>
                </div>

                {catalogLoading && <p role="status" className="p-4 text-sm text-gray-600">Caricamento pacchetti…</p>}
                {!catalogLoading && packages.length === 0 && <p className="p-4 text-sm text-gray-600">Nessun pacchetto disponibile.</p>}
                <button type="button" onClick={() => setReload(n => n + 1)} disabled={catalogLoading || isLoading} className="mx-5 mt-3 text-sm text-indigo-700">Aggiorna catalogo e saldo</button>
                {/* CARD PACCHETTI */}
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
                  {packages.map(pkg => {
                    const selected = packageId === pkg.id;
                    const pricePerSignature =
                      Math.round(pkg.net_unit_cents / pkg.credits_per_package);

                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() =>
                          handleSelectPackage(pkg.id)
                        }
                        disabled={isLoading}
                        aria-pressed={selected}
                        className={`relative rounded-xl border-2 p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                          selected
                            ? 'border-indigo-300 bg-indigo-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                        }`}
                      >
                        {/* QUANTITÀ PACCHETTO */}
                        <div className="flex items-start justify-between gap-3">

                          <div>
                            <div className="text-sm font-bold text-gray-900">
                              {pkg.name}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              {formatCurrency(pricePerSignature)} per firma
                            </div>
                          </div>

                          {/* SELEZIONE */}
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                              selected
                                ? 'border-indigo-500 bg-indigo-500'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {selected && (
                              <CheckCircleOutlineIcon
                                sx={{ fontSize: 16 }}
                                className="text-white"
                              />
                            )}
                          </span>
                        </div>

                        {/* PREZZO PACCHETTO */}
                        <div className="mt-4 flex items-end justify-between gap-2">

                          <div>
                            <div className="text-xl font-extrabold text-indigo-700">
                              {formatCurrency(pkg.net_unit_cents)}
                            </div>

                            <div className="mt-0.5 text-xs text-gray-500">
                              + IVA per pacchetto
                            </div>
                          </div>

                          {selected && (
                            <span className="rounded-full bg-indigo-100 px-2 py-1 text-[10px] font-bold uppercase text-indigo-700 border border-indigo-500">
                              Selezionato
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* NUMERO PACCHETTI */}
                <div className="border-t border-gray-200 bg-gray-50/60 px-4 py-4 sm:px-5">

                  <div className="flex flex-wrap items-center justify-between gap-4">

                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Numero di pacchetti
                      </p>

                      <p className="mt-1 text-xs text-gray-500">
                        Pacchetto selezionato da {quantity}{' '}
                        {quantity === 1 ? 'firma' : 'firme'}
                      </p>
                    </div>

                    {/* SELETTORE QUANTITÀ */}
                    <div className="inline-flex items-center overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-sm">

                      <button
                        type="button"
                        onClick={() =>
                          updatePackageCount(packageCount - 1)
                        }
                        disabled={packageCount <= 1 || isLoading}
                        aria-label="Diminuisci numero pacchetti"
                        className="flex h-10 w-10 items-center justify-center bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
                      >
                        <RemoveIcon fontSize="small" />
                      </button>

                      <input
                        type="number"
                        min="1"
                        max={MAX_PACKAGES}
                        step="1"
                        value={packageCount}
                        onChange={e =>
                          updatePackageCount(e.target.value)
                        }
                        disabled={isLoading}
                        aria-label="Numero pacchetti da acquistare"
                        className="h-10 w-20 border-x border-indigo-200 bg-white text-center text-sm font-bold text-indigo-800 focus:outline-none"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          updatePackageCount(packageCount + 1)
                        }
                        disabled={packageCount >= MAX_PACKAGES || isLoading}
                        aria-label="Aumenta numero pacchetti"
                        className="flex h-10 w-10 items-center justify-center bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
                      >
                        <AddIcon fontSize="small" />
                      </button>
                    </div>
                  </div>

                  {/* TOTALE FIRME */}
                  <div className="mt-4 flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3">

                    <span className="text-sm font-medium text-indigo-700">
                      Totale firme acquistate
                    </span>

                    <span className="text-lg font-bold text-indigo-900">
                      {totalSignatures}
                    </span>
                  </div>
                </div>
              </div>

              {/* RIEPILOGO ACQUISTO */}
              <div className="overflow-hidden rounded-xl border border-indigo-200 bg-white">

                <div className="border-b border-indigo-100 bg-indigo-50/50 px-4 py-2 sm:px-5">
                  <h3 className="text-sm font-semibold uppercase text-indigo-800">
                    Riepilogo acquisto
                  </h3>

                  <p className="pt-0.5 text-xs text-gray-500">
                    Verifica quantità e costo prima di procedere
                  </p>
                </div>

                <div className="divide-y divide-gray-100">

                  {/* PRODOTTO */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Prodotto
                    </span>

                    <span className="text-right text-sm font-semibold text-indigo-800">
                      Firme Elettroniche Avanzate
                    </span>
                  </div>

                  {/* PACCHETTO */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Pacchetto selezionato
                    </span>

                    <span className="text-sm font-semibold text-indigo-800">
                      {quantity} {quantity === 1 ? 'firma' : 'firme'}
                    </span>
                  </div>

                  {/* NUMERO PACCHETTI */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Numero pacchetti
                    </span>

                    <span className="text-sm font-semibold text-indigo-800">
                      {packageCount}
                    </span>
                  </div>

                  {/* QUANTITÀ TOTALE FIRME */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Totale firme
                    </span>

                    <span className="text-sm font-semibold text-indigo-800">
                      {totalSignatures}
                    </span>
                  </div>

                  {/* PREZZO PACCHETTO */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Prezzo singolo pacchetto
                    </span>

                    <span className="text-sm font-semibold text-indigo-800">
                      {packagePrice !== null
                        ? `${formatCurrency(packagePrice)} + IVA`
                        : 'Non disponibile'}
                    </span>
                  </div>

                  {/* PREZZO MEDIO PER FIRMA */}
                  <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                    <span className="text-sm text-gray-600">
                      Prezzo medio per firma
                    </span>

                    <span className="text-sm font-semibold text-indigo-800">
                      {packageUnitPrice !== null
                        ? `${formatCurrency(packageUnitPrice)} + IVA`
                        : 'Non disponibile'}
                    </span>
                  </div>
                </div>

                {quote && <div className="space-y-2 border-t px-5 py-3 text-sm">
                  <div className="flex justify-between"><span>Imponibile</span><span>{formatCurrency(quote.net)}</span></div>
                  <div className="flex justify-between"><span>IVA ({selectedPackage.vat_rate}%)</span><span>{formatCurrency(quote.vat)}</span></div>
                </div>}
                {/* TOTALE LORDO */}
                <div className="flex items-center justify-between gap-4 border-t border-indigo-200 bg-indigo-50/60 px-4 py-4 sm:px-5">

                  <div>
                    <p className="text-sm font-bold text-indigo-900">
                      Totale acquisto
                    </p>

                    <p className="mt-0.5 text-xs text-gray-500">
                      Importo IVA inclusa, prima di eventuali sconti
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-extrabold text-indigo-700">
                      {subtotal !== null
                        ? formatCurrency(subtotal)
                        : '—'}
                    </p>

                    {subtotal !== null && (
                      <p className="text-xs text-gray-500">
                        IVA inclusa
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* INFORMAZIONI */}
              <div className="flex items-start gap-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">

                <CheckCircleOutlineIcon
                  fontSize="small"
                  className="mt-0.5 shrink-0 text-indigo-600"
                />

                <p className="text-xs leading-5 text-gray-600">
                  Eventuali codici sconto si inseriscono su Stripe Checkout, che conferma il totale finale. L'acquisto riguarda uno o più pacchetti di firme FEA.
                  Il pagamento non costituisce la sottoscrizione di un nuovo
                  abbonamento. La disponibilità dei crediti sarà aggiornata
                  dopo la conferma del pagamento.
                </p>
              </div>

              {/* ERRORI */}
              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {error}
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">

              <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">

                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:opacity-50"
                >
                  Annulla
                </button>

                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canCheckout}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShoppingCartOutlinedIcon fontSize="small" />

                  {isLoading
                    ? 'Caricamento...'
                    : 'Procedi al pagamento'}
                </button>
              </div>
            </div>

          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
